import { Directive, ElementRef, Input, OnDestroy, Renderer2, inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { fromEvent, Subscription } from 'rxjs';

/**
 * Directive to handle trimming of input values and normalization of whitespace or custom characters.
 * This directive integrates seamlessly with Angular forms by implementing the `ControlValueAccessor` interface.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'input[trim]',
  standalone: false,
})
export class TrimDirective implements ControlValueAccessor, OnDestroy {
  /**
   * Enables or disables trimming functionality.
   * If set, the directive will trim the input value based on the provided configuration.
   */
  @Input() trim: string | boolean | undefined;

  /**
   * Enables or disables the normalization of consecutive whitespace characters.
   * When enabled, multiple spaces will be replaced with a single space.
   */
  @Input() trimWhitespaces: string | boolean | undefined;

  /**
   * Specifies an array of custom characters to normalize.
   * Consecutive occurrences of these characters will be replaced with a single instance.
   */
  @Input() multiCharToTrim!: string[];

  private value!: string;
  private readonly whiteSpaceRegEx: RegExp = new RegExp(/\s{2,}/g);
  private blurSubscription: Subscription;

  /**
   * Callback function to propagate changes in the input value to the parent form control.
   */
  onChange: (_: any) => void = (_: any) => {};

  /**
   * Callback function to notify the parent form control when the input is blurred.
   */
  onTouched: () => void = () => {};

  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly ngControl: NgControl | null = inject(NgControl, { optional: true, self: true });

  constructor() {
    // Subscribes to the blur event on the input element to handle value updates.
    this.blurSubscription = fromEvent<Event>(this.elementRef.nativeElement, 'blur').subscribe((event: Event) => {
      this.onBlur(event);
    });
  }

  /**
   * Registers a callback function to handle changes in the input value.
   * @param fn - The callback function to register.
   */
  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function to handle touch events on the input.
   * @param fn - The callback function to register.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Writes a value to the input element and applies trimming or normalization if necessary.
   * @param value - The value to write to the input element.
   */
  public writeValue(value: string): void {
    const trimmedValue: string = value?.trim() || '';
    this.value = trimmedValue === '' ? '' : this.replaceChars(trimmedValue) || '';
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', this.value);

    if (this.ngControl?.control) {
      this.ngControl.control.patchValue(this.value);
    }
  }

  /**
   * Handles the blur event on the input element.
   * Trims or normalizes the input value and triggers the `onTouched` callback.
   *
   * @param event - The blur event object.
   */
  private onBlur(event: Event): void {
    const inputValue: string = (event.target as HTMLInputElement).value.trim();
    this.updateValue(inputValue);
    this.onTouched();
  }

  /**
   * Updates the value of the input element and propagates changes to the parent form control.
   * @param value - The new value to set.
   */
  private updateValue(value: string): void {
    if (this.trim !== undefined) {
      value = this.trim !== '' ? value : value.trim();
      const previous: string = this.value;
      this.writeValue(value);
      if ((this.value || previous) && this.value.trim() !== previous) {
        this.onChange(this.value);
      }
    }
  }

  /**
   * Normalizes the input value by replacing multiple spaces or custom characters.
   * @param value - The value to normalize.
   * @returns The normalized value.
   */
  private replaceChars(value: string): string {
    if (this.trimWhitespaces !== undefined) {
      value = value.replace(this.whiteSpaceRegEx, ' ');
    }
    if (this.multiCharToTrim) {
      this.multiCharToTrim.forEach(char => {
        const charRegexp: RegExp = new RegExp(`[${char}]{2,}`, 'g');
        value = value.replace(charRegexp, char);
      });
    }
    return value;
  }

  /**
   * Cleans up resources by unsubscribing from the blur event listener.
   * This method is called automatically when the directive is destroyed.
   */
  ngOnDestroy(): void {
    if (this.blurSubscription) {
      this.blurSubscription.unsubscribe();
      this.blurSubscription = undefined!;
    }
  }
}
